import React, { memo, useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';
import CardContent from '@mui/material/CardContent';
import Card from '@mui/material/Card';
import MoreVertIcon from '@mui/icons-material/MoreVertRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import Content from 'studio/components/Content/Content';
import Link from 'studio/components/Link';
import { trackEvent } from 'utils/eventTracking';
import { useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import { useMessage } from 'studio/components/Message';
import { useLoader } from 'components/loaders/FadeLoader';
import services from 'services';
import styles from './Home.styles';
import { veezooUrls } from 'config/constants';
import { paths as studioPaths } from 'studio/App/Studio/Studio';
import { VeezooStudioIcon } from 'components/Icons';
import PageTitle from 'studio/components/PageTitle';
import { useContextMenu } from 'components/ContextMenu/ContextMenu';
import IconButton from '@mui/material/IconButton';

const Home = ({ classes, knowledgeGraphs, fetchKnowledgeGraphs }) => {
  useEffect(() => {
    trackEvent('Studio Home Opened');
  }, []);

  const { showContextMenu } = useContextMenu();
  const [state, setState] = useState({});

  const { successMessage, errorMessage } = useMessage();
  const { withLoader } = useLoader();
  const { showConfirmationModal } = useConfirmationModal();

  // initially (re-)retrieve the knowledge graphs
  useEffect(() => {
    fetchKnowledgeGraphs();
  }, []);

  const deleteKnowledgeGraph = useCallback(
    async knowledgeGraph => {
      const result = await withLoader(services.deleteKnowledgeGraph(knowledgeGraph.data.id));
      if (result.success) {
        successMessage('The Knowledge Graph has successfully been deleted');
        fetchKnowledgeGraphs();
      } else {
        const message = result?.response?.data?.data?.message || 'There was an error deleting the Knowledge Graph';
        errorMessage(message);
      }
    },
    [fetchKnowledgeGraphs, successMessage, errorMessage, withLoader]
  );

  const deleteAction = knowledgeGraph => [
    {
      icon: DeleteIcon,
      label: knowledgeGraph.data.developmentKgForId ? 'Delete Branch' : 'Delete Knowledge Graph',
      onClick: () =>
        showConfirmationModal(() => ({
          title: knowledgeGraph.data.developmentKgForId ? 'Delete Branch' : 'Delete Knowledge Graph',
          content: knowledgeGraph.data.developmentKgForId
            ? `Are you sure you want to delete the branch "${knowledgeGraph.data.info.default.name}"?`
            : `Are you sure you want to delete the Knowledge Graph "${knowledgeGraph.data.info.default.name}"?`,
          onConfirm: () => deleteKnowledgeGraph(knowledgeGraph)
        }))
    }
  ];

  const knowledgeGraphsGrid = knowledgeGraphs && (
    <>
      <Typography variant="h2" gutterBottom>
        Knowledge Graphs
      </Typography>
      <Grid container spacing={2} className={classes.grid}>
        {knowledgeGraphs
          .filter(knowledgeGraph => !knowledgeGraph.data.isDemo)
          .map((knowledgeGraph, i) => (
            <Grid
              item
              md={6}
              lg={3}
              key={knowledgeGraph.data.id}
              className={classes.card}
              component={Link}
              href={studioPaths.root}
              knowledgeGraph={knowledgeGraph}
            >
              <Card
                onMouseOver={() => setState({ ['elevation' + i]: 3 })}
                onMouseOut={() => setState({ ['elevation' + i]: null })}
                elevation={state['elevation' + i] || 1}
              >
                <CardContent>
                  <IconButton
                    size="small"
                    onClick={event => showContextMenu(event, deleteAction(knowledgeGraph), null, true)}
                    className={classes.more}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Typography color="textSecondary" component="div">
                    {knowledgeGraph.data.id}
                  </Typography>
                  <Typography variant="h6" component="div">
                    {knowledgeGraph.data.info.default.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    className={knowledgeGraph.data.developmentKgForId ? undefined : classes.doNotShow}
                    color="textSecondary"
                    component="div"
                  >
                    Development KG for {knowledgeGraph.data.developmentKgForName}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>
    </>
  );

  const welcomeMessage = (
    <div>
      <span>Welcome to Veezoo Studio! Here is what you can do here:</span>
      <ul>
        <li>
          <Link href={studioPaths.new}>Connect your data by creating a new Knowledge Graph.</Link>
        </li>
        <li>Model your data and business logic in the Editor, once you have created your first Knowledge Graph.</li>
      </ul>
      <span>
        Questions? Don&apos;t forget to check our{' '}
        <Link href={veezooUrls.docs} onClick={() => trackEvent('Docs Opened')} newWindow>
          Veezoo Docs
        </Link>
        {', '}
        join our{' '}
        <Link href={veezooUrls.slack} onClick={() => trackEvent('Veezoo Slack Opened')} newWindow>
          community in Slack
        </Link>{' '}
        or{' '}
        <Link href={veezooUrls.contact} onClick={() => trackEvent('Contact Us Opened')} newWindow>
          contact us directly.
        </Link>
      </span>
    </div>
  );

  const shouldShowGrid = knowledgeGraphs?.filter(knowledgeGraph => !knowledgeGraph.data.isDemo).length > 0;

  return (
    <Content data-test="homeContent">
      <Button className={classes.buttonNew} variant="contained" color="primary" component={Link} href={studioPaths.new}>
        New Knowledge Graph
      </Button>
      <PageTitle icon={VeezooStudioIcon}>Veezoo Studio</PageTitle>
      {welcomeMessage}
      {shouldShowGrid && knowledgeGraphsGrid}
    </Content>
  );
};

export default memo(withStyles(styles)(Home));
